import { z } from 'zod';

// === 안건 화이트보드 (docs/agenda-whiteboard-plan.md) ===
// 서버(WebSocket·REST)와 클라이언트가 공유하는 계약. 프로토콜을 바꿀 때는 이 파일부터 고친다.

export const WHITEBOARD_LIMITS = {
  BOARD_TTL_DAYS: 7,
  MAX_ELEMENTS: 5000,
  MAX_ELEMENT_BYTES: 262144,
  WS_MAX_PAYLOAD: 1048576,
  MAX_BOARD_FILES: 40,
  MAX_BOARD_FILE_BYTES: 1048576,
  MAX_BOARD_FILES_TOTAL_BYTES: 31457280,
  MAX_SNAPSHOT_BYTES: 3145728,
  MAX_SNAPSHOT_EDGE: 4096,
  MAX_SNAPSHOTS_PER_AGENDA: 30,
  CHAT_MAX_LENGTH: 500,
  CHAT_HISTORY: 100,
  POINTER_INTERVAL_MS: 40,
  MAX_CONNECTIONS_PER_ROOM: 60,
  MAX_CONNECTIONS_PER_USER: 3,
  /** 보드 전체 요소의 직렬화 바이트 합계 상한(tombstone 포함) — 넘기는 신규·증가분은 거부 */
  MAX_BOARD_BYTES: 16777216,
} as const;

export const WHITEBOARD_TIMINGS = {
  /** 연결 후 첫 auth 메시지 대기 */
  AUTH_TIMEOUT_MS: 5_000,
  HEARTBEAT_MS: 30_000,
  /** 클라이언트 → 서버 digest 대조 주기 (드래그·편집 중에는 건너뛴다) */
  SYNC_CHECK_INTERVAL_MS: 20_000,
  /** 마지막 변경 후 flush 지연 / 연속 변경 중 최대 flush 간격 */
  FLUSH_DEBOUNCE_MS: 2_000,
  FLUSH_MAX_INTERVAL_MS: 10_000,
  /** 마지막 참가자가 나간 뒤 메모리 언로드까지 */
  ROOM_UNLOAD_DELAY_MS: 60_000,
  /** flush 때 이보다 오래된 tombstone(isDeleted)은 버린다 */
  TOMBSTONE_TTL_MS: 24 * 60 * 60 * 1000,
} as const;

export const WHITEBOARD_WS_PATH = '/api/ws/whiteboard';

/** WebSocket 닫힘 코드 */
export const WHITEBOARD_CLOSE_CODES = {
  AUTH_FAILED: 4401,
  FORBIDDEN: 4403,
  AGENDA_NOT_FOUND: 4404,
  AUTH_TIMEOUT: 4408,
  TOO_MANY_CONNECTIONS: 4429,
  /** 서버 재시작(graceful shutdown) — 클라이언트는 재접속한다 */
  SERVICE_RESTART: 1012,
} as const;

/** 서버 → 클라이언트 `error` 메시지 코드 */
export const WHITEBOARD_ERROR_CODES = {
  INVALID_MESSAGE: 'INVALID_MESSAGE',
  TOO_MANY_ELEMENTS: 'TOO_MANY_ELEMENTS',
  ELEMENT_TOO_LARGE: 'ELEMENT_TOO_LARGE',
  BOARD_TOO_LARGE: 'BOARD_TOO_LARGE',
} as const;

/**
 * 커서 팔레트 — 라이트·다크 배경 양쪽에서 구분되는 12색.
 * 브랜드 오렌지(#FC5E20)는 선택/CTA 전용이라 제외한다.
 */
export const CURSOR_PALETTE = [
  '#E5484D', // red
  '#E93D82', // crimson
  '#AB4ABA', // plum
  '#6E56CF', // violet
  '#3E63DD', // indigo
  '#0090FF', // blue
  '#00A2C7', // cyan
  '#12A594', // teal
  '#30A46C', // green
  '#8DB600', // lime
  '#AD7F58', // brown
  '#E0A800', // amber
] as const;

export const CURSOR_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

/** 보드 이미지 file_id (Excalidraw fileId — 기본은 SHA-1 hex 40자) */
export const WHITEBOARD_FILE_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

/** 서버가 받아들이는 요소 type. embeddable·iframe·magicframe은 거부 */
export const WHITEBOARD_ELEMENT_TYPES = [
  'rectangle',
  'diamond',
  'ellipse',
  'arrow',
  'line',
  'freedraw',
  'text',
  'image',
  'frame',
] as const;

/** version 상한 — 터무니없는 값으로 요소를 영구 잠그는 것 방지 */
export const WHITEBOARD_MAX_ELEMENT_VERSION = 2 ** 31;

/** pointer에 싣는 선택 요소 id 상한(원격 선택 테두리용 — 넘으면 앞에서 자른다) */
export const WHITEBOARD_MAX_SELECTED = 200;

// ─── 요소 (서버는 해석하지 않고 최소 형태만 검증, 나머지 필드는 그대로 보존) ───

export const whiteboardElementSchema = z.looseObject({
  id: z.string().min(1).max(64),
  type: z.enum(WHITEBOARD_ELEMENT_TYPES),
  version: z.number().int().min(1).max(WHITEBOARD_MAX_ELEMENT_VERSION),
  versionNonce: z.number().int().refine(Number.isSafeInteger),
  isDeleted: z.boolean(),
  /** Excalidraw가 붙이는 마지막 수정 시각(epoch ms). tombstone 정리 기준 */
  updated: z.number().optional(),
});
export type WhiteboardElement = z.infer<typeof whiteboardElementSchema>;

/** 최소 비교 형태 — 서버·클라이언트 공용 */
export interface WhiteboardElementVersion {
  version: number;
  versionNonce: number;
  isDeleted: boolean;
}

/**
 * 병합 규칙(Excalidraw 협업과 동일): version이 큰 쪽, 같으면 versionNonce가 작은 쪽이 이긴다.
 * incoming이 existing을 대체해야 하면 true.
 */
export function isNewerWhiteboardElement(
  incoming: Pick<WhiteboardElementVersion, 'version' | 'versionNonce'>,
  existing: Pick<WhiteboardElementVersion, 'version' | 'versionNonce'> | undefined,
): boolean {
  if (!existing) return true;
  if (incoming.version !== existing.version) return incoming.version > existing.version;
  return incoming.versionNonce < existing.versionNonce;
}

/**
 * 보드 digest — 삭제되지 않은 요소의 `개수:version 합:versionNonce XOR`(순서 무관).
 * 클라이언트와 서버가 같은 식을 써서 주기적으로 대조한다.
 */
export function computeWhiteboardDigest(elements: readonly WhiteboardElementVersion[]): string {
  let count = 0;
  let versionSum = 0;
  let nonceXor = 0;
  for (const el of elements) {
    if (el.isDeleted) continue;
    count++;
    versionSum += el.version;
    nonceXor = (nonceXor ^ el.versionNonce) | 0;
  }
  return `${count}:${versionSum}:${nonceXor >>> 0}`;
}

/**
 * z-순서 비교 — fractional `index` 오름차순, `index`가 없는 요소는 뒤로, 같으면 `id` 순.
 * index는 ASCII 문자열이라 코드 유닛 비교를 쓴다(localeCompare 금지).
 */
export function compareWhiteboardElementOrder(
  a: { id: string; index?: unknown },
  b: { id: string; index?: unknown },
): number {
  const ai = typeof a.index === 'string' ? a.index : null;
  const bi = typeof b.index === 'string' ? b.index : null;
  if (ai !== bi) {
    if (ai === null) return 1;
    if (bi === null) return -1;
    return ai < bi ? -1 : 1;
  }
  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
}

/**
 * z-순서로 정렬한 새 배열. Excalidraw `restoreElements`는 배열 순서와 index 순서가 어긋난 요소의
 * index를 다시 매기며 version·versionNonce를 바꾼다 — 서버는 이 순서로 내보내고, 클라이언트는 원격 요소를
 * restore에 넘기기 전에 이 순서로 맞춘다.
 */
export function sortWhiteboardElementsByIndex<T extends { id: string; index?: unknown }>(
  elements: readonly T[],
): T[] {
  return [...elements].sort(compareWhiteboardElementOrder);
}

// ─── 클라이언트 → 서버 메시지 ───

export const wbAuthMessageSchema = z.object({
  t: z.literal('auth'),
  token: z.string().min(1).max(4096),
  agendaId: z.number().int().positive(),
});

export const wbClientMessageSchema = z.discriminatedUnion('t', [
  z.object({
    t: z.literal('elements'),
    epoch: z.string().min(1).max(36),
    elements: z.array(whiteboardElementSchema).max(WHITEBOARD_LIMITS.MAX_ELEMENTS),
  }),
  z.object({
    t: z.literal('sync:check'),
    digest: z.string().max(100),
  }),
  // 씬 좌표. 캔버스를 벗어나면 { gone: true }만 보낸다.
  // selected = 선택 요소 id 목록(원격 선택 테두리용) — 선택이 바뀐 때만 싣는다
  z.object({
    t: z.literal('pointer'),
    x: z.number().optional(),
    y: z.number().optional(),
    tool: z.enum(['pointer', 'laser']).optional(),
    button: z.enum(['up', 'down']).optional(),
    gone: z.literal(true).optional(),
    selected: z.array(z.string().min(1).max(64)).max(WHITEBOARD_MAX_SELECTED).optional(),
  }),
  z.object({
    t: z.literal('chat'),
    text: z.string().min(1).max(WHITEBOARD_LIMITS.CHAT_MAX_LENGTH),
  }),
  z.object({
    t: z.literal('clear'),
  }),
]);

export type WbAuthMessage = z.infer<typeof wbAuthMessageSchema>;
export type WbClientMessage = z.infer<typeof wbClientMessageSchema>;

// ─── 서버 → 클라이언트 메시지 ───

export interface WbPeer {
  connId: string;
  userId: number;
  name: string;
  color: string;
}

export interface WbChatMessage {
  id: string;
  connId: string;
  userId: number;
  name: string;
  color: string;
  text: string;
  /** ISO 8601 */
  ts: string;
}

export interface WbActor {
  userId: number;
  name: string;
}

export type WbServerMessage =
  | {
      t: 'init';
      self: WbPeer;
      peers: WbPeer[];
      elements: WhiteboardElement[];
      /** 서버에 저장된 보드 이미지 file_id 목록 */
      fileIds: string[];
      epoch: string;
      /** ISO 8601. 아직 한 번도 수정되지 않은 보드면 null */
      lastEditedAt: string | null;
      chat: WbChatMessage[];
    }
  | { t: 'peer:join'; peer: WbPeer }
  | { t: 'peer:leave'; connId: string }
  | { t: 'elements'; connId: string; epoch: string; elements: WhiteboardElement[] }
  | { t: 'sync:full'; epoch: string; elements: WhiteboardElement[] }
  | {
      t: 'pointer';
      connId: string;
      x?: number;
      y?: number;
      tool?: 'pointer' | 'laser';
      button?: 'up' | 'down';
      gone?: true;
      selected?: string[];
    }
  | { t: 'chat'; message: WbChatMessage }
  | { t: 'file:added'; id: string }
  | { t: 'cleared'; epoch: string; by: WbActor }
  | { t: 'snapshot:saved'; by: WbActor }
  | { t: 'error'; code: string; message: string };

export type WbServerMessageType = WbServerMessage['t'];

// ─── REST 응답 (snake_case — 기존 API 관례) ───

export interface WhiteboardParticipant {
  user_id: number;
  name: string;
  color: string;
}

/** GET /whiteboard/agendas/:agendaId/status */
export interface WhiteboardStatus {
  /** 삭제되지 않은 요소가 1개 이상 */
  has_content: boolean;
  last_edited_at: string | null;
  /** last_edited_at + 7일. 내용이 없으면 null */
  expires_at: string | null;
  /** 현재 접속 중인 사용자(사용자 단위 중복 제거) */
  participants: WhiteboardParticipant[];
  snapshot_count: number;
}

/** GET /whiteboard/agendas/:agendaId/snapshots 항목 · POST 응답 */
export interface WhiteboardSnapshot {
  id: number;
  agenda_id: number;
  mime_type: string;
  file_size: number;
  width: number | null;
  height: number | null;
  created_by: number | null;
  created_by_name: string | null;
  created_at: string;
  /** 요청자 기준 삭제 가능 여부(저장한 본인·안건 PM·master) */
  can_delete: boolean;
}

/** POST /whiteboard/agendas/:agendaId/files 응답 */
export interface WhiteboardFileUploadResult {
  id: string;
}
